// Tenantra Inc. - Disputes List Page
import { useEffect, useState } from 'react';
import { Search, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';
import api, { apiHelpers } from '@/services/api';
import { useUrlParam } from '@/services/useUrlParam';
import SubscriptionGate from '@/components/SubscriptionGate';
import { RowOpenButton } from '@/components/dashboard/TableKit';
import { DisputePanel } from '@/components/dashboard/DisputePanel';

const STATUS = {
  open: { label: 'Awaiting Response', dot: 'bg-amber-500', text: 'text-amber-600' },
  landlord_responded: { label: 'Response Submitted', dot: 'bg-blue-500', text: 'text-blue-600' },
  under_review: { label: 'Under Review', dot: 'bg-blue-500', text: 'text-blue-600' },
  resolved: { label: 'Resolved', dot: 'bg-slate-400', text: 'text-slate-500' }
};

const REASONS = {
  not_my_tenancy: 'Not My Tenancy', incorrect_dates: 'Incorrect Dates',
  incorrect_amount: 'Incorrect Amount', damage_not_caused: 'Damage Not Caused',
  payment_was_made: 'Payment Was Made', other: 'Other Reason'
};

export default function DisputesPage() {
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useUrlParam('status');
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => { fetchData(); }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setSubscriptionRequired(false);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [disputesRes, statsRes] = await Promise.all([
        api.get('/disputes', { params }),
        api.get('/disputes/stats/landlord')
      ]);
      setDisputes(disputesRes.data.items || []);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
        setSubscriptionMessage(err.response?.data?.detail?.message || 'Subscription required.');
        return;
      }
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const filtered = disputes.filter(d => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return d.tenant_name?.toLowerCase().includes(s) || d.property_address?.toLowerCase().includes(s);
  });

  if (subscriptionRequired) return <SubscriptionGate message={subscriptionMessage} />;

  return (
    <div className="space-y-3" data-testid="disputes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-semibold text-slate-900">Disputes</h1>
          <p className="text-[11px] text-slate-400">Manage tenant disputes</p>
        </div>
        <button onClick={fetchData} className="text-[11px] text-slate-400 hover:text-slate-600 font-medium inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex items-center gap-4 py-2 border-b border-slate-200 flex-wrap text-xs">
          <span><strong className="text-amber-600">{stats.awaiting_response || 0}</strong> <span className="text-slate-400">Awaiting</span></span>
          <span className="text-slate-200">|</span>
          <span><strong className="text-blue-600">{(stats.responded || 0) + (stats.under_review || 0)}</strong> <span className="text-slate-400">In review</span></span>
          <span className="text-slate-200">|</span>
          <span><strong className="text-emerald-600">{stats.resolved || 0}</strong> <span className="text-slate-400">Resolved</span></span>
          <span className="text-slate-200">|</span>
          <span><strong className="text-slate-500">{stats.total || 0}</strong> <span className="text-slate-400">Total</span></span>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8 pl-8 text-xs bg-white border-slate-200"
            data-testid="disputes-search"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 px-2.5 pr-7 bg-white border border-slate-200 rounded-md text-xs text-slate-600 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-teal-500"
            data-testid="disputes-filter"
          >
            <option value="all">All</option>
            <option value="open">Awaiting</option>
            <option value="landlord_responded">Responded</option>
            <option value="under_review">In Review</option>
            <option value="resolved">Resolved</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Action Required */}
      {stats?.awaiting_response > 0 && statusFilter === 'all' && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-center justify-between">
          <p className="text-xs text-amber-800">
            <strong>{stats.awaiting_response}</strong> dispute{stats.awaiting_response > 1 ? 's' : ''} awaiting your response
          </p>
          <button onClick={() => setStatusFilter('open')} className="text-[11px] text-amber-700 font-medium flex-shrink-0">
            View
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-5 h-5 border-2 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 border border-slate-200 rounded-md">
          <p className="text-xs font-medium text-slate-700 mb-0.5">
            {searchTerm || statusFilter !== 'all' ? 'No results' : 'No disputes'}
          </p>
          <p className="text-[11px] text-slate-400">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No disputes from tenants'}
          </p>
          {(searchTerm || statusFilter !== 'all') && (
            <button onClick={() => { setSearchTerm(''); setStatusFilter('all'); }} className="text-[11px] text-teal-600 font-medium mt-2">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
            <div className="col-span-3">Tenant</div>
            <div className="col-span-2">Property</div>
            <div className="col-span-2">Reason</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1" />
          </div>

          {/* Rows */}
          <div className="divide-y divide-slate-100">
            {filtered.map((d) => {
              const s = STATUS[d.status] || STATUS.open;
              return (
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenId(d.id)}
                  onKeyDown={(e) => e.key === 'Enter' && setOpenId(d.id)}
                  className="lens lens-row w-full grid grid-cols-12 gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors items-center text-left cursor-pointer"
                  data-testid={`dispute-row-${d.id}`}
                >
                  {/* Tenant with dot */}
                  <div className="col-span-12 sm:col-span-3 flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
                    <span className="text-xs font-medium text-slate-800 truncate">{d.tenant_name}</span>
                    <span className="sm:hidden text-[10px] text-slate-400 ml-auto">{REASONS[d.reason] || d.reason}</span>
                  </div>

                  {/* Property */}
                  <div className="hidden sm:block col-span-2">
                    <p className="text-xs text-slate-600 truncate">{d.property_address}</p>
                  </div>

                  {/* Reason */}
                  <div className="hidden sm:block col-span-2">
                    <p className="text-xs text-slate-600">{REASONS[d.reason] || d.reason}</p>
                  </div>

                  {/* Date */}
                  <div className="hidden sm:block col-span-2">
                    <p className="text-xs text-slate-500">{formatDate(d.created_at)}</p>
                  </div>

                  {/* Status */}
                  <div className="hidden sm:block col-span-2">
                    <span className={`text-xs ${s.text}`}>{s.label}</span>
                  </div>

                  <div className="hidden sm:flex col-span-1 justify-end">
                    <RowOpenButton onClick={() => setOpenId(d.id)} testId={`dispute-open-${d.id}`} label={`Open dispute from ${d.tenant_name}`} />
                  </div>

                  {/* Mobile bottom */}
                  <div className="sm:hidden col-span-12 flex items-center justify-between -mt-0.5">
                    <span className="text-[10px] text-slate-400 truncate">{d.property_address} · {formatDate(d.created_at)}</span>
                    <span className={`text-[10px] ${s.text}`}>{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <DisputePanel disputeId={openId} onClose={() => { setOpenId(null); fetchData(); }} />
    </div>
  );
}
